"use client"

import { useEffect, useMemo, useState, type ReactNode } from "react"
import { format } from "date-fns"
import { useForm, useFieldArray } from "react-hook-form"
import { Link } from "@tanstack/react-router"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_LABEL, FIELD_UNDERLINE } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  User,
  Building2,
  Calendar,
  DollarSign,
  FileText,
  Users,
  Mail,
  Edit,
  Trash2,
  MapPin,
  Phone,
  Save,
  X,
  Plus,
} from "lucide-react"
import type {
  PlacementSplit,
  PlacementFeeType,
  PlacementSplitOwnerType,
  PlacementPlacementType,
  User as AuthUser
} from "openapi/api/model"
import {
  getGetOrgObsRevenueQueryKey,
  getGetPlacementsQueryKey,
  useDeletePlacementsId,
  useGetAuthUser,
  useGetCandidatesCandidateIdTimeline,
  useGetContacts,
  useGetPlacements,
  usePutPlacementsId,
} from "openapi/api/endpoints"
import { PlacementTimelineCard } from "./placement-timeline-card"
import { toast } from "sonner"
import {
  buildPlacementUpdateBody,
  getPlacementCreatedAt,
  getPlacementRecordId,
  type PlacementListRow,
} from "@/utils/placements"
import { useQueryClient } from "@tanstack/react-query"

interface PlacementDetailDialogProps {
  isOpen: boolean
  onClose: () => void
  placementId: string
}

type FeeTypeUi = "percentage" | "fixed"

type SplitFormRow = {
  id?: string
  owner_name: string
  owner_type: PlacementSplitOwnerType
  percentage: number | "" // allow empty input
}

type EditPlacementForm = {
  salary: number | ""
  start_date: string | "" // ✅ editable (YYYY-MM-DD)
  end_date: string | ""   // ✅ editable (YYYY-MM-DD)
  fee_amount: number | ""
  fee_type: FeeTypeUi
  notes: string
  splits: SplitFormRow[]
}

type PlacementWithCandidate = PlacementListRow & {
  candidate_id?: string | null
}

function safeParseDate(value?: string | null): Date | null {
  if (!value) return null

  // Treat YYYY-MM-DD as LOCAL date to avoid off-by-one
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (m) {
    const y = Number(m[1])
    const mo = Number(m[2]) - 1
    const d = Number(m[3])
    const local = new Date(y, mo, d)
    return Number.isNaN(local.getTime()) ? null : local
  }

  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatMoney(amount: number, currencySymbol = "$"): string {
  return `${currencySymbol}${Math.round(amount).toLocaleString()}`
}

function toUiFeeType(t: PlacementFeeType): FeeTypeUi {
  return t === "fixed" ? "fixed" : "percentage"
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n)
}

function sumPercent(splits: SplitFormRow[]): number {
  return splits.reduce((acc, s) => acc + (isFiniteNumber(s.percentage) ? s.percentage : 0), 0)
}

function mapSplitsToForm(splits?: PlacementSplit[]): SplitFormRow[] {
  if (!splits || splits.length === 0) {
    return [
      {
        owner_name: "",
        owner_type: "recruiter",
        percentage: 100,
      },
    ]
  }

  return splits.map((s) => ({
    id: s.id,
    owner_name: s.owner_name ?? "Unknown",
    owner_type: s.owner_type,
    percentage: s.percentage,
  }))
}

function isValidIsoDateOnly(value: string): boolean {
  // YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const d = safeParseDate(value)
  return d != null
}

const CONTROL = cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")
const AREA = cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")

/**
 * A section of the record, not a card. The dialog is already the surface;
 * cards inside it were surfaces inside a surface, each with its own border and
 * shadow, which is what made this read as a page pasted into a modal.
 *
 * At module scope, and it has to be. Defined inside the dialog it was a new
 * component type on every render, so React unmounted and remounted every
 * section each time — and `watch("splits")` re-renders on each keystroke, so a
 * split input was replaced after its first letter and took the focus with it.
 */
function Section({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cn("mt-6 border-t pt-5 first:mt-0 first:border-t-0 first:pt-0", className)}>
      {children}
    </section>
  )
}

export function PlacementDetailDialog({ isOpen, onClose, placementId }: PlacementDetailDialogProps) {
  const queryClient = useQueryClient()
  const [isEditingPlacement, setIsEditingPlacement] = useState(false)
  const { data: user } = useGetAuthUser()
  const { data: placements } = useGetPlacements()
  const placement = placements?.placements?.find((p) => {
    return getPlacementRecordId(p as PlacementWithCandidate) === placementId
  }) as PlacementWithCandidate | undefined
  const placementRecordId = getPlacementRecordId(placement) || placementId

  useEffect(() => {
    setIsEditingPlacement(false)
  }, [placementId])

  useEffect(() => {
    if (!isOpen) {
      setIsEditingPlacement(false)
    }
  }, [isOpen])

  const candidateId = placement?.candidate_id ?? ""

  const { data: candidateContactInfo } = useGetContacts(
    { candidateId },
    { query: { enabled: !!candidateId } },
  )

  const { data: timelineData } = useGetCandidatesCandidateIdTimeline(
    candidateId,
    { jobId: placement?.job_id },
    { query: { enabled: !!candidateId } },
  )

  const { mutateAsync: updatePlacement, isPending: isSaving } = usePutPlacementsId()
  const { mutateAsync: deletePlacement, isPending: isDeleting } = useDeletePlacementsId()

  const createdAt = useMemo(() => safeParseDate(getPlacementCreatedAt(placement)), [placement])
  const startDate = useMemo(() => safeParseDate(placement?.start_date ?? null), [placement?.start_date])
  const endDate = useMemo(() => safeParseDate(placement?.end_date ?? null), [placement?.end_date])

  const splits = useMemo<PlacementSplit[]>(() => placement?.splits ?? [], [placement?.splits])
  const salary = placement?.salary ?? null

  const calculatedFee = useMemo(() => {
    if (placement?.fee_type === "percentage") {
      if (salary == null) return null
      return (placement?.fee_amount * salary) / 100
    }
    return placement?.fee_amount
  }, [placement?.fee_type, placement?.fee_amount, salary])

  const candidateName = placement?.candidate_name ?? "Unknown candidate"
  const candidateEmail =
    candidateContactInfo?.[0]?.email && candidateContactInfo?.[0]?.email !== "enrich_layer_not_found"
      ? candidateContactInfo?.[0]?.email
      : ""
  const candidatePhone =
    candidateContactInfo?.[0]?.phone && candidateContactInfo?.[0]?.phone !== "enrich_layer_not_found"
      ? candidateContactInfo?.[0]?.phone
      : ""

  const companyName = placement?.company_name ?? "Unknown company"
  const jobTitle = placement?.job_title ?? "Unknown role"
  const jobId = placement?.job_id ?? ""
  const currencySymbol = "$"

  const feeLabel =
    placement?.fee_type === "percentage"
      ? `${placement?.fee_amount}%`
      : `${formatMoney(placement?.fee_amount ?? 0, currencySymbol)} fixed`

  const splitTotal = useMemo(() => splits.reduce((acc, s) => acc + s.percentage, 0), [splits])
  const splitTotalIsValid = splitTotal === 100

  const defaultEditValues: EditPlacementForm = useMemo(
    () => ({
      salary: placement?.salary ?? "",
      start_date: placement?.start_date ?? "",
      end_date: placement?.end_date ?? "",
      fee_amount: placement?.fee_amount ?? "",
      fee_type: toUiFeeType(placement?.fee_type as PlacementFeeType),
      notes: placement?.notes ?? "",
      splits: mapSplitsToForm(placement?.splits),
    }),
    [placement?.salary, placement?.start_date, placement?.end_date, placement?.fee_amount, placement?.fee_type, placement?.notes, placement?.splits],
  )

  const { control, register, handleSubmit, reset, setValue, watch } = useForm<EditPlacementForm>({
    defaultValues: defaultEditValues,
    mode: "onChange",
  })

  useEffect(() => {
    if (!isEditingPlacement) {
      reset(defaultEditValues)
    }
  }, [defaultEditValues, isEditingPlacement, reset])

  const { fields: splitFields, append, remove } = useFieldArray({
    control,
    name: "splits",
  })

  const watchedSplits = watch("splits")
  const editSplitTotal = useMemo(() => sumPercent(watchedSplits ?? []), [watchedSplits])

  const watchedFeeType = watch("fee_type")
  const watchedFeeAmount = watch("fee_amount")
  const watchedSalary = watch("salary")
  const watchedStartDate = watch("start_date")
  const watchedEndDate = watch("end_date")

  const startEditingPlacement = () => {
    reset(defaultEditValues)
    setIsEditingPlacement(true)
  }

  const cancelEditingPlacement = () => {
    reset(defaultEditValues)
    setIsEditingPlacement(false)
  }

  const onSavePlacement = async (data: EditPlacementForm) => {
    if (!placement) {
      toast.error("Missing placement", { description: "Placement data not loaded yet." })
      return
    }

    // salary validation
    const salaryValue = isFiniteNumber(data.salary) ? data.salary : null
    if (salaryValue != null && salaryValue < 0) {
      toast.error("Salary must be positive")
      return
    }

    // date validation
    const startDateValue = data.start_date === "" ? null : data.start_date
    const endDateValue = data.end_date === "" ? null : data.end_date

    if (startDateValue != null && !isValidIsoDateOnly(startDateValue)) {
      toast.error("Invalid start date", { description: "Use YYYY-MM-DD." })
      return
    }
    if (endDateValue != null && !isValidIsoDateOnly(endDateValue)) {
      toast.error("Invalid end date", { description: "Use YYYY-MM-DD." })
      return
    }
    if (startDateValue != null && endDateValue != null) {
      const sd = safeParseDate(startDateValue)
      const ed = safeParseDate(endDateValue)
      if (sd && ed && ed.getTime() < sd.getTime()) {
        toast.error("Invalid dates", { description: "End date must be after start date." })
        return
      }
    }

    // fee validation
    if (!isFiniteNumber(data.fee_amount)) {
      toast.error("Fee is required", { description: "Enter a valid fee amount." })
      return
    }
    if (data.fee_amount < 0) {
      toast.error("Fee must be positive")
      return
    }
    if (data.fee_type === "percentage" && data.fee_amount > 100) {
      toast.warning("Incorrect fee", { description: "Percentage fee must be <= 100." })
      return
    }

    // salary required if percentage
    if (data.fee_type === "percentage" && salaryValue == null) {
      toast.error("Salary required", { description: "Salary must be set when fee type is percentage." })
      return
    }

    // splits validation + cleanup
    const cleanedSplits = (data.splits ?? [])
      .map((s) => ({ ...s, owner_name: (s.owner_name ?? "").trim() }))
      .filter((s) => s.owner_name.length > 0)

    if (cleanedSplits.length === 0) {
      toast.error("Splits are required", { description: "Add at least one split owner." })
      return
    }

    const pct = sumPercent(cleanedSplits)
    if (Math.abs(pct - 100) > 0.01) {
      toast.warning("Incorrect split", { description: "Split percentages must sum to 100%." })
      return
    }

    const body = buildPlacementUpdateBody({
      job_candidate_id: placement?.job_candidate_id as string,
      placement_type: placement?.placement_type as PlacementPlacementType,

      salary: salaryValue,
      start_date: startDateValue,
      end_date: endDateValue,

      fee_amount: data.fee_amount,
      fee_type: data.fee_type,

      notes: data.notes.trim() || null,

      splits: cleanedSplits.map((s) => ({
        owner_name: s.owner_name,
        owner_type: s.owner_type,
        percentage: isFiniteNumber(s.percentage) ? s.percentage : 0,
        ...(s.owner_name === "Me" && {
          owner_id: (user as unknown as { user: AuthUser }).user.id,
        }),
      })),
    })

    try {
      await updatePlacement({
        id: placementRecordId,
        data: body,
      })

      toast.success("Updated", { description: "Placement details saved." })
      setIsEditingPlacement(false)

      await queryClient.invalidateQueries({ queryKey: getGetPlacementsQueryKey(), exact: false })
      await queryClient.invalidateQueries({ queryKey: getGetOrgObsRevenueQueryKey(), exact: false })
    } catch {
      toast.error("Update failed", { description: "Please try again." })
    }
  }

  const previewCalculatedFee = useMemo(() => {
    if (!isEditingPlacement) return null
    if (!isFiniteNumber(watchedFeeAmount)) return null

    const s = isFiniteNumber(watchedSalary) ? watchedSalary : null

    if (watchedFeeType === "fixed") return watchedFeeAmount
    if (s == null) return null
    return (watchedFeeAmount * s) / 100
  }, [isEditingPlacement, watchedFeeAmount, watchedFeeType, watchedSalary])

  const handleDeletePlacement = async () => {
    if (!placementRecordId) {
      toast.error("Missing placement", { description: "Placement id is required to delete." })
      return
    }

    try {
      await deletePlacement({ id: placementRecordId })
      await queryClient.invalidateQueries({ queryKey: getGetPlacementsQueryKey(), exact: false })
      await queryClient.invalidateQueries({ queryKey: getGetOrgObsRevenueQueryKey(), exact: false })
      toast.success("Deleted", { description: "Placement deleted successfully." })
      onClose()
    } catch {
      toast.error("Delete failed", { description: "Please try again." })
    }
  }

  return (
    <DialogShell
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      wide
      title="Placement Details"
      /*
        The actions were a row inside the title band, which put Delete beside
        the heading and above everything it deletes. They belong in the footer,
        where every other dialog keeps them, with the destructive one held away
        from the pair you reach for by habit.
      */
      footer={
        isEditingPlacement ? (
          <>
            <QuietAction size="md" onClick={cancelEditingPlacement} disabled={isSaving}>
              <X className="size-[14px]" />
              Cancel
            </QuietAction>
            <PageAction
              tone="primary"
              onClick={handleSubmit(onSavePlacement)}
              disabled={isSaving}
            >
              <Save className="size-[15px]" />
              {isSaving ? "Saving…" : "Save"}
            </PageAction>
          </>
        ) : (
          <>
            <QuietAction
              size="md"
              tone="destructive"
              onClick={handleDeletePlacement}
              disabled={isDeleting}
              className="@sm:mr-auto"
            >
              <Trash2 className="size-[14px]" />
              {isDeleting ? "Deleting…" : "Delete"}
            </QuietAction>
            <PageAction tone="primary" onClick={startEditingPlacement}>
              <Edit className="size-[15px]" />
              Edit
            </PageAction>
          </>
        )
      }
    >
        <div>
          {/* Header Info */}
          <div className="flex flex-col justify-between gap-4 border-b pb-5 @lg:flex-row @lg:items-center">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <User className="h-7 w-7 text-muted-foreground" />
              </div>

              <div>
                <h2 className="text-xl font-semibold">
                  {candidateId ? (
                    <Link
                      to="/candidates"
                      search={{ candidateId }}
                      className="font-semibold text-brand-ink transition-colors hover:text-brand-ink/80 hover:underline"
                    >
                      {candidateName}
                    </Link>
                  ) : (
                    candidateName
                  )}
                </h2>
                {candidateEmail ? <p className="text-muted-foreground">{candidateEmail}</p> : <p className="text-muted-foreground">No email on file</p>}

                <div className="flex items-center gap-2 mt-1">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div className="text-sm">
                    {jobId ? (
                      <>
                        <Link
                          to="/jobs/$jobId/$view"
                          params={{ jobId, view: "overview" }}
                          className="font-semibold text-brand-ink transition-colors hover:text-brand-ink/80 hover:underline"
                        >
                          {jobTitle}
                        </Link>
                        <span className="text-muted-foreground"> @ </span>
                        <Link
                          to="/jobs/$jobId/$view"
                          params={{ jobId, view: "overview" }}
                          className="font-semibold text-brand-ink transition-colors hover:text-brand-ink/80 hover:underline"
                        >
                          {companyName}
                        </Link>
                      </>
                    ) : (
                      <span className="font-semibold">{jobTitle} @ {companyName}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <Badge variant="secondary" className="text-sm">
                {placement?.placement_type}
              </Badge>
            </div>
          </div>

          {/* Key Metrics */}
          {/*
            Grid cells, not sections. A section draws a hairline above itself
            unless it is first, which in a two-column grid gave the second
            cell of each row a rule and a margin the first did not have — so
            Fee sat half a line below Salary.
          */}
          <div className="grid grid-cols-1 gap-x-6 gap-y-6 pt-5 @lg:grid-cols-2">
            {/* Salary card: editable */}
            <div className="min-w-0">
              <div className="mb-3 flex flex-row items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-[12px] font-medium">
                  <DollarSign className="size-3.5 text-muted-foreground" />
                  Salary
                </h3>
              </div>

              <div>
                {!isEditingPlacement ? (
                  <>
                    <div className="text-2xl font-bold">{salary == null ? "—" : formatMoney(salary, currencySymbol)}</div>
                    <p className="text-xs text-muted-foreground">{placement?.placement_type}</p>
                  </>
                ) : (
                  <div className="space-y-1">
                    <div className={FIELD_LABEL}>Salary</div>
                    <input
                    aria-label="Salary"
                      type="number"
                      step="1"
                      placeholder="200000"
                      {...register("salary", { setValueAs: (v) => (v === "" ? "" : Number(v)) })}
                    className={CONTROL}
                  />
                    <p className="text-xs text-muted-foreground">Required if Fee Type is percentage.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Fee card: editable */}
            <div className="min-w-0">
              <div className="mb-3 flex flex-row items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-[12px] font-medium">
                  <DollarSign className="size-3.5 text-muted-foreground" />
                  Fee
                </h3>
              </div>

              <div>
                {!isEditingPlacement ? (
                  <>
                    <div className="text-2xl font-bold">{calculatedFee == null ? "—" : formatMoney(calculatedFee, currencySymbol)}</div>
                    <p className="text-xs text-muted-foreground">{feeLabel}</p>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <div className={FIELD_LABEL}>{watch("fee_type") === "percentage" ? "Fee (%)" : "Fee (amount)"}</div>
                        <input
                    aria-label="Fee amount"
                          type="number"
                          step="0.01"
                          {...register("fee_amount", { setValueAs: (v) => (v === "" ? "" : Number(v)) })}
                    className={CONTROL}
                  />
                      </div>

                      <div className="space-y-1">
                        <div className={FIELD_LABEL}>Fee Type</div>
                        <Select
                          value={watch("fee_type")}
                          onValueChange={(v) => setValue("fee_type", v as FeeTypeUi, { shouldDirty: true })}
                        >
                          <SelectTrigger aria-label="Fee type" className={cn(CONTROL, "h-auto")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage</SelectItem>
                            <SelectItem value="fixed">Fixed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Preview:{" "}
                      <span className="font-medium">
                        {previewCalculatedFee == null ? "—" : formatMoney(previewCalculatedFee, currencySymbol)}
                      </span>
                      {watch("fee_type") === "percentage" && !isFiniteNumber(watchedSalary) ? (
                        <span className="ml-2">(no salary set)</span>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Start/End Date card: ✅ editable */}
            <div className="min-w-0">
              <div className="mb-3 flex flex-row items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-[12px] font-medium">
                  <Calendar className="size-3.5 text-muted-foreground" />
                  Start / End
                </h3>
              </div>
              <div>
                {!isEditingPlacement ? (
                  <>
                    <div className="text-lg font-semibold">{startDate ? format(startDate, "MMM dd, yyyy") : "—"}</div>
                    <p className="text-xs text-muted-foreground">
                      {endDate ? `Ends ${format(endDate, "MMM dd, yyyy")}` : "Ongoing"}
                    </p>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <div className={FIELD_LABEL}>Start Date</div>
                      <input
                    aria-label="Start date"
                        type="date"
                        {...register("start_date")}
                    className={CONTROL}
                  />
                    </div>

                    <div className="space-y-1">
                      <div className={FIELD_LABEL}>End Date</div>
                      <input
                    aria-label="End date"
                        type="date"
                        {...register("end_date")}
                    className={CONTROL}
                  />
                    </div>

                    {/* Tiny inline feedback */}
                    {watchedStartDate && !isValidIsoDateOnly(watchedStartDate) ? (
                      <div className="text-xs text-destructive">Invalid start date</div>
                    ) : null}
                    {watchedEndDate && !isValidIsoDateOnly(watchedEndDate) ? (
                      <div className="text-xs text-destructive">Invalid end date</div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-0">
              <div className="mb-3 flex flex-row items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-[12px] font-medium">
                  <Users className="size-3.5 text-muted-foreground" />
                  Team Split
                </h3>
              </div>
              <div>
                <div className="text-lg font-semibold">{splits.length} members</div>
                <p className="text-xs text-muted-foreground">
                  {splits.length === 0 ? "No splits" : splitTotalIsValid ? "Sums to 100%" : `Sums to ${splitTotal}%`}
                </p>
              </div>
            </div>
          </div>

          {/* Candidate Information */}
          <Section>
            <div className="mb-3 flex flex-row items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 text-[12px] font-medium">
                <User className="size-3.5 text-muted-foreground" />
                Candidate Information
              </h3>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 @lg:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Email:</span>
                    <span className="text-sm">{candidateEmail || "—"}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Phone:</span>
                    <span className="text-sm">{candidatePhone || "—"}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Location:</span>
                    <span className="text-sm">—</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Company:</span>
                    <span className="text-sm">{companyName}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Position:</span>
                    <span className="text-sm">{jobTitle}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Created:</span>
                    <span className="text-sm">{createdAt ? format(createdAt, "MMM dd, yyyy") : "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Revenue Splits */}
          <Section>
            <div className="mb-3 flex flex-row items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 text-[12px] font-medium">
                <DollarSign className="size-3.5 text-muted-foreground" />
                Revenue Splits
              </h3>

              {isEditingPlacement ? (
                <button
                  type="button"
                  onClick={() => append({ owner_name: "", owner_type: "recruiter", percentage: "" })}
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-brand-ink transition-opacity hover:opacity-80"
                >
                  <Plus className="size-3.5" />
                  Add split
                </button>
              ) : null}
            </div>

            <div>
              {!isEditingPlacement ? (
                splits.length === 0 ? (
                  <p className="text-[12px] text-muted-foreground">No splits configured.</p>
                ) : (
                  <div>
                    {!splitTotalIsValid && (
                      <div className="mb-2 text-[12px] text-destructive">
                        Split percentages must sum to 100%. Current total: {splitTotal}%.
                      </div>
                    )}

                    {splits.map((split) => {
                      const ownerName = split.owner_name ?? "Unknown"
                      const ownerType = split.owner_type
                      const percent = split.percentage
                      const payout = calculatedFee == null ? null : Math.round((calculatedFee * percent) / 100)

                      return (
                        <div
                          key={split.id}
                          className="flex items-center justify-between gap-3 border-t py-2.5 first:border-t-0"
                        >
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span className="flex size-8 flex-none items-center justify-center rounded-full bg-primary/10">
                              <User className="size-3.5 text-brand-ink" />
                            </span>
                            <div className="min-w-0">
                              <div className="truncate text-[12.5px] font-medium leading-[1.3]">{ownerName}</div>
                              <div className="text-[11.5px] capitalize leading-[1.4] text-muted-foreground">
                                {ownerType}
                              </div>
                            </div>
                          </div>

                          {/* Machine values, so mono — like every other figure in the app. */}
                          <div className="flex-none text-right">
                            <div className="font-mono text-[12.5px] font-medium">{percent}%</div>
                            <div className="font-mono text-[11px] text-muted-foreground">
                              {payout == null ? "—" : formatMoney(payout, currencySymbol)}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  <div className={cn("grid grid-cols-12 gap-3", FIELD_LABEL)}>
                    <div className="col-span-6">Owner</div>
                    <div className="col-span-3">Owner Type</div>
                    <div className="col-span-2">Percentage</div>
                    <div className="col-span-1"></div>
                  </div>

                  {splitFields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-12 items-end gap-3">
                      <div className="col-span-6">
                        <div className="flex items-center gap-2">
                          <input
                            {...register(`splits.${index}.owner_name` as const)}
                            placeholder="Owner name"
                            aria-label={`Split ${index + 1} owner`}
                            className={CONTROL}
                          />

                          {/* Me quick-fill */}
                          <QuietAction
                            onClick={() =>
                              setValue(`splits.${index}.owner_name` as const, "Me", {
                                shouldDirty: true,
                                shouldTouch: true,
                              })
                            }
                            className="h-7 flex-none px-2"
                          >
                            Me
                          </QuietAction>
                        </div>
                      </div>

                      <div className="col-span-3">
                        <Select
                          value={watch(`splits.${index}.owner_type` as const)}
                          onValueChange={(v) =>
                            setValue(`splits.${index}.owner_type` as const, v as PlacementSplitOwnerType, {
                              shouldDirty: true,
                            })
                          }
                        >
                          <SelectTrigger
                            aria-label={`Split ${index + 1} owner type`}
                            className={cn(CONTROL, "h-auto")}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recruiter">Recruiter</SelectItem>
                            <SelectItem value="referrer">Referrer</SelectItem>
                            <SelectItem value="agency">Agency</SelectItem>
                            <SelectItem value="external">External</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-2">
                        <input
                          type="number"
                          step="0.01"
                          {...register(`splits.${index}.percentage` as const, {
                            setValueAs: (v) => (v === "" ? "" : Number(v)),
                          })}
                          placeholder="100"
                          aria-label={`Split ${index + 1} percentage`}
                          className={cn(CONTROL, "font-mono")}
                        />
                      </div>

                      <div className="col-span-1">
                        {splitFields.length > 1 ? (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            aria-label={`Remove split ${index + 1}`}
                            className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-destructive"
                          >
                            <X className="size-4" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}

                  <div className="text-[11.5px] text-muted-foreground">
                    Total{" "}
                    <span className={cn("font-mono font-medium", editSplitTotal !== 100 && "text-destructive")}>
                      {editSplitTotal}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* Notes */}
          {isEditingPlacement || placement?.notes ? (
            <Section>
              <div className="mb-3 flex flex-row items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-[12px] font-medium">
                  <FileText className="h-5 w-5" />
                  Notes
                </h3>
              </div>
              <div>
                {isEditingPlacement ? (
                  <textarea
                    aria-label="Notes"
                    placeholder="Add placement notes..."
                    rows={4}
                    className={AREA}
                    {...register("notes")}
                  />
                ) : (
                  <p className="text-sm leading-relaxed">{placement?.notes}</p>
                )}
              </div>
            </Section>
          ) : null}

          {/* Timeline */}
          <PlacementTimelineCard
            createdAt={createdAt}
            startDate={placement?.start_date}
            endDate={placement?.end_date}
            items={timelineData}
            filterToPlacementDates={false}
          />
        </div>
    </DialogShell>
  )
}
